import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiError } from "../../../shared/api/client";
import { AppButton } from "../../../shared/components/ui";
import { useFieldHeader, useFieldJourney } from "../../../shared/components/FieldShell";
import {
  IconCheck,
  IconError,
  IconHourglass,
  IconSpinner,
  IconWarning,
} from "../../../shared/components/icons";
import { getInstallation, submitInstallation } from "../api";
import { enqueueInst, readPaymentCache } from "../offline";
import type { InstallationSubmitError } from "../types";

/**
 * S5 — Submit gate (handoff TASK-008, s5-submit-*).
 * Checklist reflects SERVER state (serial, geofence distance, photos, customer
 * confirmation) + the device-cached payment GO. Submit POSTs with a stable
 * per-mount Idempotency-Key (retry/replay safe). 422 surfaces missing[],
 * ledger_unreachable (fail-closed, distinct) and geofence details; offline
 * queues the submit for the next reconnect.
 */

const GEOFENCE_LIMIT_M = 3;

const MISSING_LABELS: Record<string, string> = {
  serial_confirmation: "Serial confirmation",
  gps_evidence: "GPS in range",
  photos: "Both photos",
  customer_confirmation: "Customer confirmation",
  payment_go: "Payment GO",
};

type CheckState = "done" | "missing" | "unknown";

interface CheckRow {
  key: string;
  label: string;
  state: CheckState;
  detail?: string;
}

export function S5SubmitGate() {
  const { id = "" } = useParams();
  const installationId = Number(id);
  const navigate = useNavigate();
  useFieldHeader("Submit installation", "Evidence checklist");
  useFieldJourney({
    flow: "install",
    step: 4,
    backTo: `/field/installations/${id}/payment`,
  });

  // Stable per-mount — retrying replays the same key (server dedupes).
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const [queued, setQueued] = useState(false);

  const query = useQuery({
    queryKey: ["installation", installationId],
    queryFn: () => getInstallation(installationId),
    retry: 1,
    staleTime: 10_000,
  });

  const mutation = useMutation({
    mutationFn: () => submitInstallation(installationId, idempotencyKey),
  });

  const inst = query.data?.installation;
  const payment = useMemo(() => readPaymentCache(installationId), [installationId]);

  const checks: CheckRow[] = useMemo(() => {
    if (!inst) return [];
    const photoTypes = new Set((inst.photos ?? []).map((p) => p.photo_type));
    const bothPhotos = photoTypes.has("installed_device") && photoTypes.has("serial_closeup");
    const dist = inst.survey_distance_m;
    const paymentGo =
      payment && payment.depositRequired !== null
        ? payment.amountPaid >= payment.depositRequired
          ? "done"
          : "missing"
        : ("unknown" as CheckState);
    return [
      {
        key: "serial",
        label: "Device serial confirmed",
        state: inst.serial_confirmed ? "done" : "missing",
      },
      {
        key: "gps",
        label: "GPS in range of survey point",
        state:
          dist !== null ? (dist <= GEOFENCE_LIMIT_M ? "done" : "missing") : "missing",
        detail:
          dist !== null
            ? `${dist.toFixed(1)} m (limit ${GEOFENCE_LIMIT_M} m)`
            : "not captured",
      },
      {
        key: "photos",
        label: "Evidence photos (installed device + serial)",
        state: bothPhotos ? "done" : "missing",
      },
      {
        key: "confirm",
        label: "Customer confirmation",
        state: inst.customer_confirmed ? "done" : "missing",
        detail: inst.customer_confirmed
          ? inst.confirmation_method === "pin"
            ? "PIN"
            : "Signature"
          : undefined,
      },
      {
        key: "payment",
        label: "Payment GO",
        state: paymentGo,
        detail:
          paymentGo === "unknown" ? "run the payment check first" : undefined,
      },
    ];
  }, [inst, payment]);

  const clientMissing = checks.filter((c) => c.state !== "done");

  const handleSubmit = () => {
    if (!navigator.onLine) {
      enqueueInst({
        queueId: crypto.randomUUID(),
        kind: "submit",
        installationId,
        idempotencyKey,
        createdAt: new Date().toISOString(),
      });
      setQueued(true);
      return;
    }
    mutation.mutate();
  };

  // ── Loading / error ────────────────────────────────────────────────────────
  if (query.isPending) {
    return (
      <div className="pg-state">
        <span className="state-icon info">
          <IconSpinner size={40} />
        </span>
        <h2>Loading checklist…</h2>
      </div>
    );
  }

  if (query.isError || !inst) {
    return (
      <div className="pg-state">
        <span className="state-icon error">
          <IconError size={40} />
        </span>
        <h2>Could not load task</h2>
        <p>{query.error instanceof Error ? query.error.message : "Something went wrong"}</p>
        <AppButton variant="secondary" type="button" onClick={() => void query.refetch()}>
          Retry
        </AppButton>
      </div>
    );
  }

  // ── Queued (offline) ───────────────────────────────────────────────────────
  if (queued) {
    return (
      <div className="pg-state">
        <span className="state-icon info">
          <IconHourglass size={40} />
        </span>
        <h2>Submission queued</h2>
        <p>The installation will be submitted automatically when back online.</p>
        <AppButton
          variant="secondary"
          type="button"
          onClick={() => navigate("/field/installations")}
        >
          Back to tasks
        </AppButton>
      </div>
    );
  }

  // ── Success ────────────────────────────────────────────────────────────────
  if (mutation.isSuccess) {
    return (
      <div className="pg-state">
        <span className="state-icon success">
          <IconCheck size={40} />
        </span>
        <h2>Installation submitted</h2>
        <p>
          Evidence bundle received ({mutation.data.reference}).
          <br />
          Awaiting office key approval — the first token is issued by the office.
        </p>
        <AppButton type="button" onClick={() => navigate("/field/installations")}>
          Back to tasks
        </AppButton>
      </div>
    );
  }

  // ── Submitting ─────────────────────────────────────────────────────────────
  if (mutation.isPending) {
    return (
      <div className="pg-state">
        <span className="state-icon info">
          <IconSpinner size={40} />
        </span>
        <h2>Submitting evidence…</h2>
      </div>
    );
  }

  // ── 422 / 409 from the server gate ─────────────────────────────────────────
  const apiErr = mutation.error instanceof ApiError ? mutation.error : null;
  const details = (apiErr?.details ?? {}) as unknown as InstallationSubmitError;
  const serverMissing = Array.isArray(details.missing) ? details.missing : null;

  if (apiErr?.status === 422 && details.ledger_unreachable) {
    return (
      <div className="pg-state">
        <span className="state-icon error">
          <IconError size={40} />
        </span>
        <h2>Submission blocked</h2>
        <p>
          Ledger unreachable — the payment gate is fail-closed, so the installation cannot be
          submitted until the ledger responds. Nothing was submitted; retry shortly.
        </p>
        <AppButton variant="secondary" type="button" onClick={() => mutation.mutate()}>
          Retry
        </AppButton>
      </div>
    );
  }

  if (apiErr?.status === 422 && details.geofence) {
    return (
      <>
        <div className="pg-state">
          <span className="state-icon warning">
            <IconWarning size={40} />
          </span>
          <h2>Submission blocked</h2>
          <p>
            GPS is {details.geofence.distance_m.toFixed(1)} m from the survey point (limit{" "}
            {details.geofence.limit_m} m). Re-capture GPS at the installed device.
          </p>
        </div>
        <div className="pg-actions">
          <AppButton
            variant="secondary"
            type="button"
            onClick={() => navigate(`/field/installations/${installationId}/evidence`)}
          >
            Back to evidence
          </AppButton>
          <AppButton
            variant="secondary"
            type="button"
            onClick={() => navigate("/field/installations")}
          >
            Save draft &amp; complete later
          </AppButton>
        </div>
      </>
    );
  }

  if (apiErr?.status === 422 && serverMissing) {
    return (
      <>
        <Checklist rows={checks} />
        <div className="pg-inline-error" role="alert">
          <IconWarning size={14} />
          <span>
            Server rejected the submission — missing:
            <ul>
              {serverMissing.map((m) => (
                <li key={m}>
                  {MISSING_LABELS[m] ?? m}
                  {m === "payment_go" && details.payment?.amount_paid !== undefined
                    ? ` (paid TZS ${details.payment.amount_paid})`
                    : ""}
                </li>
              ))}
            </ul>
          </span>
        </div>
        <div className="pg-actions">
          <AppButton variant="secondary" type="button" onClick={() => mutation.mutate()}>
            Retry submission
          </AppButton>
          <AppButton
            variant="secondary"
            type="button"
            onClick={() => navigate("/field/installations")}
          >
            Save draft &amp; complete later
          </AppButton>
        </div>
      </>
    );
  }

  if (apiErr?.status === 409) {
    return (
      <div className="pg-state">
        <span className="state-icon warning">
          <IconWarning size={40} />
        </span>
        <h2>Already submitted</h2>
        <p>{apiErr.message}</p>
        <AppButton
          variant="secondary"
          type="button"
          onClick={() => navigate("/field/installations")}
        >
          Back to tasks
        </AppButton>
      </div>
    );
  }

  if (mutation.isError) {
    return (
      <div className="pg-state">
        <span className="state-icon error">
          <IconError size={40} />
        </span>
        <h2>Submission failed</h2>
        <p>
          {mutation.error instanceof Error ? mutation.error.message : "Server unreachable."}
          <br />
          Your evidence is safe on this device.
        </p>
        <AppButton variant="secondary" type="button" onClick={() => mutation.mutate()}>
          Retry
        </AppButton>
        <button
          type="button"
          className="pg-linklike"
          onClick={() => {
            enqueueInst({
              queueId: crypto.randomUUID(),
              kind: "submit",
              installationId,
              idempotencyKey,
              createdAt: new Date().toISOString(),
            });
            setQueued(true);
          }}
        >
          or save offline &amp; sync later
        </button>
      </div>
    );
  }

  // ── Default: checklist + submit ────────────────────────────────────────────
  return (
    <>
      <Checklist rows={checks} />

      <p className="pg-draft-note" style={{ color: "var(--pg-text-muted)" }}>
        Submitting bundles this evidence into the dMRV installation record.
      </p>

      {clientMissing.length > 0 ? (
        <div className="pg-inline-error" role="alert">
          <IconWarning size={14} />
          Cannot submit — missing: {clientMissing.map((c) => c.label).join(", ")}
        </div>
      ) : null}

      <div className="pg-actions">
        {clientMissing.length > 0 ? (
          <AppButton
            variant="secondary"
            type="button"
            onClick={() => navigate("/field/installations")}
          >
            Save draft &amp; complete later
          </AppButton>
        ) : (
          <AppButton type="button" onClick={handleSubmit}>
            Submit for key approval
          </AppButton>
        )}
      </div>
    </>
  );
}

function Checklist({ rows }: { rows: CheckRow[] }) {
  return (
    <div className="pg-checklist">
      {rows.map((r) => (
        <div key={r.key} className={`row ${r.state}`}>
          <span className="check-icon">
            {r.state === "done" ? (
              <IconCheck size={16} />
            ) : r.state === "missing" ? (
              <IconError size={16} />
            ) : (
              <IconHourglass size={16} />
            )}
          </span>
          <span className="check-label">
            {r.label}
            {r.detail ? (
              <>
                {" "}
                <span style={{ fontWeight: 400, color: "var(--pg-text-muted)" }}>({r.detail})</span>
              </>
            ) : null}
          </span>
          <span className="check-state">
            {r.state === "done" ? "Done" : r.state === "missing" ? "Missing" : "Check"}
          </span>
        </div>
      ))}
    </div>
  );
}
